import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { routeUrls } from "../../config/routeUrls";
import imageServicesBag from "../../assets/images/icons/services_bag.svg";
import { ActionSidebarsContext } from "../../context/actionSidebarsContext";
import { IActionSidebarsContext } from "../../types/actionSidebars";
import { IServicesContext, ServicesContext } from "../../context/servicesContext";
import { getMainServicesStatusIcon } from "../../utils/helpers/statusIcons";
import Button from "../ui/button/Button";
import Skeleton from "@mui/material/Skeleton";

interface ServicesWidgetProps {
  services?: any;
}

const ServicesWidget: React.FC<ServicesWidgetProps> = ({ services: propsServices }) => {
  const { t } = useTranslation();
  
  const { setSidebarAddServiceOpen } = useContext(ActionSidebarsContext) as IActionSidebarsContext;
  const contextServices = useContext(ServicesContext) as IServicesContext;
  
  // Usa i dati dalle props se forniti, altrimenti dal context
  const finalServices = propsServices || contextServices.services;

  // Controlla se i dati dei servizi sono ancora in caricamento
  const isServicesDataLoading = !finalServices || finalServices.mainServices.length === 0;

  const renderSkeletonLoader = () => {
    return (
      <div className="col-span-12 md:col-span-6 lg:col-span-12 xl:col-span-6 md:flex md:flex-col md:justify-between rounded-[8px] bg-bgGrey p-4">
        <div>
          <Skeleton variant="text" width="20%" height={16} sx={{ marginBottom: 1 }} />
          <Skeleton variant="text" width="60%" height={22} sx={{ marginBottom: 2 }} />
        </div>
        <div className="flex items-center text-sm leading-[17px]">
          <Skeleton variant="text" width="30%" height={16} sx={{ marginRight: 1 }} />
          <div className="flex items-center">
            <Skeleton variant="circular" width={16} height={16} sx={{ marginRight: 1 }} />
            <Skeleton variant="text" width="25%" height={16} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-[56px] md:mb-0 md:bg-lightGrey md:p-6 md:rounded card-shadow">
      <div className="sm:flex sm:justify-between sm:items-center sm:mb-6">
        <h2 className="font-light text-[24px] mb-4 sm:mb-0 leading-[38px]">
          {t("screens.dashboard.widgets.services.heading")}
        </h2>
        <Button
          className="mb-6 sm:mb-0"
          callback={() => setSidebarAddServiceOpen(true)}
        >
          <div className="flex items-center">
            <span className="mr-[12px]">
              {t("common.buttonActions.services.addServices")}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-[18px] h-[18px]"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v12m6-6H6"
              />
            </svg>
          </div>
        </Button>
      </div>

      {isServicesDataLoading ? (
        <div className="grid grid-cols-12 gap-6">
          {renderSkeletonLoader()}
          {renderSkeletonLoader()}
        </div>
      ) : (
        <>
          {finalServices && finalServices.mainServices.length > 0 && (
            <>
              <div className="grid grid-cols-12 gap-6">
                {finalServices.mainServices.map((service: any, index: number) => {
                  return (
                    <div
                      key={index}
                      className="col-span-12 md:col-span-6 lg:col-span-12 xl:col-span-6 md:flex md:flex-col md:justify-between rounded-[8px] bg-bgGrey p-4"
                    >
                      <div>
                        <div className="text-red uppercase text-sm mb-1 font-semibold">
                          {service.type}
                        </div>
                        <div className="text-black font-bold text-[18px] leading-[22px] mb-4">
                          {service.name}
                        </div>
                      </div>
                      <div className="flex items-center text-sm leading-[17px]">
                        <div className="mr-[10px]">
                          {t("screens.dashboard.widgets.services.status")}:
                        </div>
                        <div className="flex items-center">
                          <div>
                            {getMainServicesStatusIcon(service.statusCode)}
                          </div>
                          <div className="font-bold ml-1">
                            {service.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div>
                {finalServices.additionalServices.length > 0 && (
                  <div className="flex items-center pb-4 border-b mt-6">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      className="w-[18px] h-[18px] stroke-red"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v12m6-6H6"
                      />
                    </svg>
                    <div className="text-lg leading-[21.6px] ml-[10px] mb-[3px]">
                      <strong>
                        {finalServices.additionalServices.length.toString()}
                      </strong>{" "}
                      {finalServices.additionalServices.length === 1
                        ? t(
                            "screens.dashboard.widgets.services.additionalServiceSingular"
                          )
                        : t(
                            "screens.dashboard.widgets.services.additionalServices"
                          )}
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <Link
                    className="inline-flex items-center"
                    to={routeUrls.services}
                  >
                    <img src={imageServicesBag} alt="services_icon_bag" />
                    <div className="uppercase text-sm leading-[17px] underline text-red font-semibold ml-[6px] mb-[2px]">
                      {t("screens.dashboard.widgets.services.link")}
                    </div>
                  </Link>
                </div>
              </div>
            </>
          )}

          {finalServices?.mainServices.length === 0 && (
            <div>{t("screens.services.emptyServices")}</div>
          )}
        </>
      )}
    </div>
  );
};

export default ServicesWidget; 